import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Briefcase, UserList, Flask, ChartBar, Gear, Headset, Bell, SignOut, Coins, Warning, X, CalendarCheck, Wrench, ShieldCheck, ListChecks, HeartStraight, Ticket, Spinner, Megaphone, Lightbulb, FileText, List as ListIcon } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import WorkspaceIndicator from "@/components/WorkspaceIndicator";
import QuotaBadge from "@/components/QuotaBadge";
import QuotaBanner from "@/components/QuotaBanner";
import ImpersonateBanner from "@/components/ImpersonateBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { to: "/candidates", label: "Candidati", icon: UserList },
  { to: "/employees", label: "Dipendenti", icon: Users },
  { to: "/positions", label: "Posizioni Aperte", icon: Briefcase },
  { to: "/calendario", label: "Calendario", icon: CalendarCheck },
  { to: "/reports/candidates", label: "Report Candidati", icon: ChartBar },
  { to: "/reports/employees", label: "Report Dipendenti", icon: ChartBar },
  { to: "/simulazione-assunzione", label: "Simulazione Assunzione", icon: Flask },
  { to: "/settings", label: "Impostazioni", icon: Gear },
  { to: "/support", label: "Supporto", icon: Headset },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Utenti", icon: ShieldCheck },
  { to: "/admin/workspaces", label: "Workspaces", icon: Briefcase },
  { to: "/admin/generations", label: "Generazioni", icon: Spinner },
  { to: "/admin/reports-affected", label: "Report pre-HMAC", icon: Warning },
  { to: "/admin/proposals", label: "Proposte", icon: Lightbulb },
  { to: "/admin/release-notes", label: "Release Notes", icon: Megaphone },
  { to: "/admin/audit-log", label: "Audit Log", icon: ListChecks },
  { to: "/admin/terms-acceptances", label: "Accettazioni Termini", icon: FileText },
  { to: "/admin/tickets", label: "Ticket", icon: Ticket },
  { to: "/admin/health", label: "Health", icon: HeartStraight },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifCount, setNotifCount] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [accountStatus, setAccountStatus] = useState(null);
  const [bannerHidden, setBannerHidden] = useState(false);
  const [antiAbuseStatus, setAntiAbuseStatus] = useState(null);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications/badge`, { withCredentials: true });
        setNotifCount(data.unread || 0);
        setPendingTasks(data.pending_tasks || 0);
      } catch {
        try {
          const { data } = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
          setNotifCount(data.count || 0);
        } catch {}
      }
    };
    const fetchAccountStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/account/status`, { withCredentials: true });
        setAccountStatus(data);
      } catch {}
    };
    fetchNotifs();
    fetchAccountStatus();
    // B82 P0.2: banner anti-abuso
    (async () => {
      try {
        const { data } = await axios.get(`${API}/workspace/current/anti-abuse-status`, { withCredentials: true });
        setAntiAbuseStatus(data);
      } catch {}
    })();
    // C18: release notes toast (1 volta per versione)
    (async () => {
      try {
        const { data } = await axios.get(`${API}/release-notes/current`, { withCredentials: true });
        if (!data || !data.id || !data.show_toast) return;
        const seenKey = `selly_release_seen_${data.version}`;
        if (localStorage.getItem(seenKey)) return;
        toast(`Novità: ${data.title}`, {
          duration: 12000,
          description: data.description || "",
          action: { label: "Ho letto", onClick: () => { localStorage.setItem(seenKey, "1"); } },
          onDismiss: () => localStorage.setItem(seenKey, "1"),
          onAutoClose: () => localStorage.setItem(seenKey, "1"),
        });
      } catch {}
    })();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data);
    } catch {}
  };

  // BUG-20: refresh badge counter (chiamato dopo mark-read/mark-all-read)
  const refreshBadge = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setNotifCount(data.count || 0);
    } catch {}
  };

  const isUnread = (n) => (n?.read === false) || (n && n.read === undefined && (n.read_at == null || n.read_at === ""));

  const markOneRead = async (nid) => {
    try {
      await axios.put(`${API}/notifications/${nid}/read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map(n => n.id === nid ? { ...n, read: true, read_at: new Date().toISOString() } : n));
      refreshBadge();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() })));
      refreshBadge();
    } catch {}
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  // B72 TASK E: stato per drawer mobile (<768px)
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Componente sidebar riusabile (sia desktop fisso che mobile drawer)
  const SidebarBody = ({ onItemClick }) => (
    <>
      <div className="p-5 pb-3">
        <h1 className="text-white text-2xl font-bold tracking-tight">SELLY<span className="text-[#56A3F5]">®</span></h1>
        <p className="text-white/40 text-xs mt-1">HR Platform</p>
      </div>
      <WorkspaceSwitcher />
      <ScrollArea className="flex-1 px-3">
        <nav className="space-y-0.5 py-2">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`}
              onClick={onItemClick}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isActive ? 'bg-[#1B4FD8] text-white shadow-lg shadow-[#1B4FD8]/20' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
              <Icon size={20} weight="duotone" />
              {label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <div className="mt-6 pt-4 border-t border-white/10" data-testid="admin-console-section">
              <div className="flex items-center gap-2 px-3 mb-2 text-[11px] font-bold text-[#56A3F5] uppercase tracking-wider">
                <Wrench size={14} weight="duotone" />
                Console Admin
              </div>
              {ADMIN_NAV.map(({ to, label, icon: Icon }) => (
                <NavLink key={to} to={to} data-testid={`admin-nav-${to.split('/').pop()}`}
                  onClick={onItemClick}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isActive ? 'bg-red-600 text-white shadow-lg shadow-red-600/20' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                  <Icon size={20} weight="duotone" />
                  {label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
      </ScrollArea>
      <div className="p-3 border-t border-white/10">
        <button onClick={handleLogout} data-testid="logout-btn"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/50 hover:text-white hover:bg-white/5 w-full transition-all">
          <SignOut size={20} /> Esci
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Sidebar desktop (>= md) */}
      <aside className="hidden md:flex w-[260px] bg-[#0D1F3C] flex-col shrink-0" data-testid="sidebar">
        <SidebarBody />
      </aside>

      {/* Sidebar mobile drawer (< md) */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-[280px] p-0 bg-[#0D1F3C] border-r-0 [&>button]:text-white" data-testid="mobile-sidebar">
          <SheetHeader className="sr-only">
            <SheetTitle>Menu di navigazione</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col h-full">
            <SidebarBody onItemClick={() => setMobileNavOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden bg-[#F4F7FB] min-w-0">
        <ImpersonateBanner />
        {/* Header */}
        <header className="h-16 bg-white border-b border-[#0D1F3C]/8 flex items-center justify-between px-3 sm:px-6 shrink-0 gap-2" data-testid="header">
          {/* Hamburger mobile only */}
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-2 rounded-lg hover:bg-[#0D1F3C]/5 transition-colors flex-shrink-0"
            data-testid="mobile-menu-btn"
            aria-label="Apri menu"
          >
            <ListIcon size={22} className="text-[#0D1F3C]" weight="bold" />
          </button>
          <div className="hidden md:block" />
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="hidden sm:block"><WorkspaceIndicator /></div>
            <QuotaBadge user={user} />
            <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
              <SheetTrigger asChild>
                <button onClick={() => { setNotifOpen(true); loadNotifications(); }} data-testid="notifications-btn"
                  className="relative p-2 rounded-lg hover:bg-[#0D1F3C]/5 transition-colors">
                  <Bell size={20} className="text-[#0D1F3C]" weight="duotone" />
                  {notifCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{notifCount}</span>}
                </button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <div className="flex items-center justify-between">
                    <SheetTitle>Notifiche</SheetTitle>
                    {notifications.some(isUnread) && (
                      <button onClick={markAllRead} data-testid="mark-all-read-btn"
                        className="text-xs text-[#1B4FD8] hover:underline font-medium">
                        Segna tutte come lette
                      </button>
                    )}
                  </div>
                </SheetHeader>
                <ScrollArea className="h-[calc(100vh-100px)] mt-4">
                  {notifications.length === 0 ? <p className="text-sm text-[#0D1F3C]/50 text-center py-8">Nessuna notifica</p> :
                    notifications.map(n => {
                      const unread = isUnread(n);
                      return (
                        <button key={n.id} onClick={() => { if (unread) markOneRead(n.id); if (n.link) navigate(n.link); setNotifOpen(false); }}
                          data-testid={`notif-${n.id}`}
                          className={`w-full text-left p-3 rounded-lg mb-2 border transition-colors ${unread ? 'bg-[#56A3F5]/5 border-[#1B4FD8]/20 hover:bg-[#56A3F5]/10' : 'bg-white border-[#0D1F3C]/5 hover:bg-[#F4F7FB]'}`}>
                          <div className="flex items-start gap-2">
                            {unread && <span className="w-2 h-2 rounded-full bg-[#1B4FD8] flex-shrink-0 mt-1.5" />}
                            <div className="flex-1 min-w-0">
                              {n.title && <p className={`text-sm ${unread ? 'font-semibold text-[#0D1F3C]' : 'text-[#0D1F3C]/80'}`}>{n.title}</p>}
                              <p className={`text-sm ${unread ? 'text-[#0D1F3C]/80' : 'text-[#0D1F3C]/60'}`}>{n.message}</p>
                              <p className="text-xs text-[#0D1F3C]/40 mt-1">{new Date(n.created_at).toLocaleDateString('it-IT')}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </ScrollArea>
              </SheetContent>
            </Sheet>
            <div className="flex items-center gap-2 pl-2 sm:pl-3 sm:border-l border-[#0D1F3C]/10">
              <div className="w-8 h-8 rounded-full bg-[#1B4FD8] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="hidden sm:block min-w-0">
                <p className="text-sm font-medium text-[#0D1F3C] leading-tight truncate">{user?.first_name} {user?.last_name}</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[11px] text-[#0D1F3C]/50 truncate">{user?.company_name}</p>
                  {/* Batch 62-bis: badge tipo account customer-facing */}
                  {(user?.account_type === "agency" || user?.account_type === "business") && (
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${
                        user.account_type === "agency"
                          ? "bg-[#1B4FD8] text-white"
                          : "bg-[#0D1F3C]/10 text-[#0D1F3C]"
                      }`}
                      title={user.account_type === "agency" ? "Account Agenzia (multi-workspace)" : "Account Azienda (single workspace)"}
                      data-testid="account-type-badge"
                    >
                      {user.account_type === "agency" ? "Agenzia" : "Azienda"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          {/* Account expiry banner */}
          {accountStatus?.show_banner && !bannerHidden && (
            <div className={`px-6 py-3 flex items-center justify-between ${accountStatus.is_frozen ? 'bg-red-500' : 'bg-[#F59E0B]'}`} data-testid="expiry-banner">
              <div className="flex items-center gap-2">
                <Warning size={18} className="text-white" weight="bold" />
                <span className="text-sm font-medium text-white">
                  {accountStatus.is_frozen
                    ? "Il tuo account e scaduto. Attiva un piano per continuare a usare SELLY®."
                    : `Il tuo account scade tra ${accountStatus.days_left} giorni. Attiva un piano per continuare senza interruzioni.`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => navigate('/settings')} className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-xs font-medium rounded-lg transition-all">Attiva Piano</button>
                {!accountStatus.is_frozen && <button onClick={() => setBannerHidden(true)} className="p-1 hover:bg-white/20 rounded text-white/70"><X size={16} /></button>}
              </div>
            </div>
          )}
          {/* B82 P0.2 — Banner anti-abuso */}
          {antiAbuseStatus?.degraded && (
            <div className="px-6 py-3 flex items-center justify-between bg-amber-100 border-b border-amber-300" data-testid="anti-abuse-banner">
              <div className="flex items-center gap-2">
                <Warning size={18} className="text-amber-800" weight="bold" />
                <div className="text-sm text-amber-900">
                  <span className="font-medium">Il workspace è in modalità protetta:</span>
                  {" "}i punteggi vengono mostrati in tre fasce grossolane (Bassa / Media / Alta) invece delle cinque abituali.
                  {antiAbuseStatus.reason === "rate_limit_hourly" || antiAbuseStatus.reason === "rate_limit_daily"
                    ? " Motivo: numero elevato di analisi in poco tempo."
                    : antiAbuseStatus.reason === "synthetic_input"
                    ? " Motivo: risposte molto simili tra loro rilevate."
                    : ""}
                  {antiAbuseStatus.resumes_at && (
                    <> Rientro automatico entro <strong>{new Date(antiAbuseStatus.resumes_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</strong>.</>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="p-3 sm:p-6">
            <QuotaBanner user={user} />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
